import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  deleteUser
} from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getAnalytics, isSupported as isAnalyticsSupported, logEvent } from 'firebase/analytics'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
}

// Check if Firebase is configured
const isFirebaseConfigured = firebaseConfig.apiKey && firebaseConfig.apiKey !== 'undefined'

// Initialize Firebase only if configured
let app = null
let auth = null
let db = null
let storage = null
let googleProvider = null
let appleProvider = null
let analytics = null

if (isFirebaseConfigured) {
  app = initializeApp(firebaseConfig)
  auth = getAuth(app)
  db = getFirestore(app)
  storage = getStorage(app)
  googleProvider = new GoogleAuthProvider()
  appleProvider = new OAuthProvider('apple.com')
  appleProvider.addScope('email')
  appleProvider.addScope('name')
  if (firebaseConfig.measurementId) {
    isAnalyticsSupported()
      .then((supported) => { if (supported) analytics = getAnalytics(app) })
      .catch(() => {})
  }
} else {
  console.warn('Firebase not configured. Running in demo mode.')
}

// Analytics: no-op until Firebase Analytics is configured and supported
export const trackEvent = (name, params = {}) => {
  if (!analytics) return
  try {
    logEvent(analytics, name, params)
  } catch (error) {
    console.warn('[FIREBASE] Analytics event failed:', error.message)
  }
}

// Auth functions
export const signInWithEmail = async (email, password) => {
  console.log('[FIREBASE] signInWithEmail called for:', email)
  if (!isFirebaseConfigured) {
    console.log('[FIREBASE] Firebase not configured!')
    return { user: null, error: 'Firebase not configured. Please add your credentials to .env file.' }
  }
  try {
    console.log('[FIREBASE] Calling signInWithEmailAndPassword...')
    const result = await signInWithEmailAndPassword(auth, email, password)
    console.log('[FIREBASE] Sign in SUCCESS:', result.user?.email)
    return { user: result.user, error: null }
  } catch (error) {
    console.log('[FIREBASE] Sign in ERROR:', error.message)
    return { user: null, error: error.message }
  }
}

export const signUpWithEmail = async (email, password, displayName) => {
  if (!isFirebaseConfigured) {
    return { user: null, error: 'Firebase not configured. Please add your credentials to .env file.' }
  }
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    if (displayName) {
      await updateProfile(result.user, { displayName })
    }
    return { user: result.user, error: null }
  } catch (error) {
    return { user: null, error: error.message }
  }
}

export const signInWithGoogle = async () => {
  console.log('[FIREBASE] signInWithGoogle called')
  if (!isFirebaseConfigured) {
    console.log('[FIREBASE] Firebase not configured!')
    return { user: null, error: 'Firebase not configured. Please add your credentials to .env file.' }
  }
  try {
    console.log('[FIREBASE] Opening Google sign-in popup...')
    const result = await signInWithPopup(auth, googleProvider)
    console.log('[FIREBASE] Google sign in SUCCESS:', result.user?.email)
    return { user: result.user, error: null }
  } catch (error) {
    console.log('[FIREBASE] Google sign in ERROR:', error.message)
    return { user: null, error: error.message }
  }
}

export const signInWithApple = async () => {
  console.log('[FIREBASE] signInWithApple called')
  if (!isFirebaseConfigured) {
    console.log('[FIREBASE] Firebase not configured!')
    return { user: null, error: 'Firebase not configured. Please add your credentials to .env file.' }
  }
  try {
    console.log('[FIREBASE] Opening Apple sign-in popup...')
    const result = await signInWithPopup(auth, appleProvider)
    console.log('[FIREBASE] Apple sign in SUCCESS:', result.user?.email)
    return { user: result.user, error: null }
  } catch (error) {
    console.log('[FIREBASE] Apple sign in ERROR:', error.message)
    return { user: null, error: error.message }
  }
}

// Handle redirect result (call this on app init)
export const handleRedirectResult = async () => {
  if (!isFirebaseConfigured) {
    return { user: null, error: null }
  }
  try {
    console.log('[FIREBASE] Checking for redirect result...')
    const result = await getRedirectResult(auth)
    if (result) {
      console.log('[FIREBASE] Redirect sign in SUCCESS:', result.user?.email)
      return { user: result.user, error: null }
    }
    console.log('[FIREBASE] No redirect result')
    return { user: null, error: null }
  } catch (error) {
    console.log('[FIREBASE] Redirect result ERROR:', error.message)
    return { user: null, error: error.message }
  }
}

export const signOut = async () => {
  console.log('[FIREBASE] signOut called')
  if (!isFirebaseConfigured) {
    console.log('[FIREBASE] Demo mode - returning success')
    return { error: null }
  }
  try {
    console.log('[FIREBASE] Calling firebaseSignOut...')
    await firebaseSignOut(auth)
    console.log('[FIREBASE] Sign out SUCCESS')
    return { error: null }
  } catch (error) {
    console.log('[FIREBASE] Sign out ERROR:', error.message)
    return { error: error.message }
  }
}

export const onAuthChange = (callback) => {
  console.log('[FIREBASE] onAuthChange - Setting up listener, isFirebaseConfigured:', isFirebaseConfigured)
  if (!isFirebaseConfigured) {
    // In demo mode, immediately call callback with null user
    console.log('[FIREBASE] Demo mode - calling callback with null')
    callback(null)
    return () => {} // Return empty unsubscribe function
  }
  return onAuthStateChanged(auth, (user) => {
    console.log('[FIREBASE] onAuthStateChanged fired - user:', user?.email || null)
    callback(user)
  })
}

export const deleteAccount = async () => {
  if (!isFirebaseConfigured) {
    return { error: 'Firebase not configured.' }
  }
  try {
    const user = auth.currentUser
    if (!user) {
      return { error: 'No user logged in' }
    }
    await deleteUser(user)
    return { error: null }
  } catch (error) {
    // If user needs to re-authenticate
    if (error.code === 'auth/requires-recent-login') {
      return { error: 'Please log out and log back in before deleting your account.' }
    }
    return { error: error.message }
  }
}

export { app, auth, db, storage, isFirebaseConfigured }
